import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Share2 } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import TradeCard from '../components/TradeCard';
import ShareToForumModal from '../components/ShareToForumModal';
import InlineError from '../components/InlineError';
import { CardListSkeleton } from '../components/SkeletonLoader';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function TradeHistoryPage() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const symbolFilter = new URLSearchParams(location.search).get('symbol') || null;
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [shareModal, setShareModal] = useState({ open: false, attachments: [], title: '', lifecycle: 'idea' });

  const fetchTrades = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const response = await axios.get(`${API_URL}/trade/history`);
      setTrades(response.data || []);
    } catch (error) {
      console.error('Error fetching trade history:', error);
      setFetchError("Couldn't load trade history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTrades(); }, []);

  const mapTrade = (t) => ({
    id: t.trade_id,
    symbol: t.symbol,
    company_name: t.company_name || t.symbol?.replace('.NS', ''),
    buyPrice: t.price,
    entry_price: t.price,
    sellPrice: t.exit_price,
    exit_price: t.exit_price,
    quantity: t.quantity,
    pnl: t.pnl,
    pnlPct: t.pnl_pct,
    entryDate: t.executed_at || null,
    exitDate: t.exit_date || null,
    status: t.status || 'Closed',
    strategyTags: t.strategy_tags || [],
  });

  const openPnLSummary = () => {
    const closed = trades.filter(t => t.status === 'CLOSED' || t.pnl != null);
    const wins = closed.filter(t => (t.pnl || 0) > 0);
    const snapshot = {
      total_trades: closed.length,
      total_pnl: closed.reduce((s, t) => s + (t.pnl || 0), 0),
      win_rate_pct: closed.length > 0 ? Math.round((wins.length / closed.length) * 100) : 0,
      avg_win: wins.length > 0 ? wins.reduce((s, t) => s + t.pnl, 0) / wins.length : null,
      avg_loss: null,
      best_trade_pnl: closed.length > 0 ? Math.max(...closed.map(t => t.pnl || 0)) : null,
      worst_trade_pnl: closed.length > 0 ? Math.min(...closed.map(t => t.pnl || 0)) : null,
    };
    setShareModal({
      open: true,
      attachments: [{ attach_type: 'pnl_summary', snapshot }],
      title: `P&L Summary — ${closed.length} trades, ₹${Math.abs(Math.round(snapshot.total_pnl)).toLocaleString('en-IN')} ${snapshot.total_pnl >= 0 ? 'profit' : 'loss'}`,
      lifecycle: snapshot.total_pnl >= 0 ? 'profit' : 'loss',
    });
  };

  const openTradeSingle = (t) => {
    const snapshot = [{
      id: t.trade_id,
      symbol: t.symbol,
      trade_type: t.trade_type || 'BUY',
      pnl: t.pnl,
      pnl_pct: t.pnl_pct,
      entry_price: t.price,
      exit_price: t.exit_price,
      entry_date: t.executed_at,
      exit_date: t.exit_date,
      strategy_tags: t.strategy_tags || [],
    }];
    const sym = t.symbol?.replace('.NS', '') || '';
    const pnlStr = t.pnl != null ? ` · ₹${Math.abs(Math.round(t.pnl)).toLocaleString('en-IN')} ${t.pnl >= 0 ? 'profit' : 'loss'}` : '';
    setShareModal({
      open: true,
      attachments: [{ attach_type: 'trade_history', snapshot }],
      title: `Trade: ${sym}${pnlStr}`,
      lifecycle: t.pnl >= 0 ? 'profit' : 'loss',
    });
  };

  if (loading) return <CardListSkeleton count={5} height="h-20" />;

  const closedTrades = trades.filter(t => t.status === 'CLOSED');
  const displayedTrades = symbolFilter
    ? closedTrades.filter(t => t.symbol?.replace('.NS', '') === symbolFilter || t.symbol === symbolFilter)
    : closedTrades;

  return (
    <div className="space-y-4" data-testid="trade-history-page">
      {/* Header */}
      <div data-tour="history-header" className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg"
            style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" style={{ color: colors.primaryText }} />
          </button>
          <div>
            <h1 className="text-xl font-bold" style={{ color: colors.primaryText }}>
              {symbolFilter ? `${symbolFilter} Trades` : 'Trade History'}
            </h1>
            <p className="text-sm" style={{ color: colors.secondaryText }}>{displayedTrades.length} completed trade{displayedTrades.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        {trades.length > 0 && (
          <button
            data-tour="history-share-pnl"
            onClick={openPnLSummary}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold"
            style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}>
            <Share2 className="w-3 h-3" /> Share P&L
          </button>
        )}
      </div>

      {/* Trade Cards */}
      {fetchError ? (
        <InlineError message={fetchError} onRetry={fetchTrades} />
      ) : displayedTrades.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-sm" style={{ color: colors.secondaryText }}>
            {symbolFilter ? `No completed trades for ${symbolFilter}` : 'No completed trades yet'}
          </p>
        </div>
      ) : (
        <div data-tour="history-trade-list" className="space-y-3">
          {displayedTrades.map((trade) => (
            <TradeCard
              key={trade.trade_id}
              trade={mapTrade(trade)}
              onClick={() => navigate(`/trade/${trade.trade_id}`)}
              onShare={() => openTradeSingle(trade)}
            />
          ))}
        </div>
      )}

      {displayedTrades.length > 0 && (
        <p className="text-xs text-center" style={{ color: colors.secondaryText }}>
          Tap a trade to view detailed analysis · <span style={{ color: '#f59e0b' }}>Share icon</span> to post to feed
        </p>
      )}

      <ShareToForumModal
        isOpen={shareModal.open}
        onClose={() => setShareModal(s => ({ ...s, open: false }))}
        attachments={shareModal.attachments}
        defaultTitle={shareModal.title}
        defaultLifecycle={shareModal.lifecycle}
      />
    </div>
  );
}

export default TradeHistoryPage;
